// src/pages/HomePage.jsx
import React from 'react';
import { Camera, Search, BarChart3, ShieldAlert, Bell, History, Heart, Settings } from 'lucide-react';

const HomePage = () => {
  const features = [
    {
      icon: Camera,
      title: 'Barcode Scanner',
      description: 'Instantly scan any product barcode to get detailed nutritional information, Nutri-Score, and ingredients',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: Search,
      title: 'Product Search',
      description: 'Search millions of products by name from the Open Food Facts database',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: BarChart3,
      title: 'Nutrition Tracker',
      description: 'Log your meals and track daily calories, protein, carbs, and fat with visual progress bars',
      color: 'from-orange-500 to-amber-500',
    },
    {
      icon: ShieldAlert,
      title: 'Allergen Alerts',
      description: 'Get instant warnings for allergens like gluten, dairy, nuts, and more in scanned products',
      color: 'from-red-500 to-pink-500',
    },
    {
      icon: Bell,
      title: 'Meal Reminders',
      description: 'Daily notifications for breakfast, lunch, dinner, and hydration to keep you on track',
      color: 'from-purple-500 to-indigo-500',
    },
    {
      icon: History,
      title: 'Scan History',
      description: 'Keep track of all your scanned products and access them anytime, even offline',
      color: 'from-teal-500 to-green-500',
    },
    {
      icon: Heart,
      title: 'Favorites',
      description: 'Save your go-to products for quick access and easy comparison',
      color: 'from-pink-500 to-rose-500',
    },
    {
      icon: Settings,
      title: 'Personalized',
      description: 'Customize your experience with dark mode, dietary preferences, and notification settings',
      color: 'from-gray-500 to-slate-500',
    },
  ];

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="text-center py-12">
        <div className="inline-block p-4 bg-gradient-to-br from-green-500 to-blue-500 rounded-3xl mb-6 shadow-xl">
          <Camera className="w-20 h-20 text-white" />
        </div>
        <h1 className="text-5xl font-bold text-gray-900 mb-4">
          Welcome to NutriLens Pro
        </h1>
        <p className="text-xl text-gray-600 mb-4 max-w-2xl mx-auto">
          Your ultimate companion for making informed food choices. Scan barcodes, track your nutrition,
          get allergen alerts, and build healthier eating habits.
        </p>
        <p className="text-lg text-green-600 font-semibold mb-8">
          Free to download. No account required.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href="https://apps.apple.com/us/app/nutrilens-pro/id6754198852"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block hover:opacity-80 transition-opacity"
          >
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              className="h-14"
            />
          </a>
        </div>
      </section>

      {/* Features Grid */}
      <section>
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
          Powerful Features
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-xl mx-auto">
          Everything you need to understand what's in your food and make better choices every day.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">
          How It Works
        </h2>
        <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 text-green-700 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
              1
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Scan or Search</h3>
            <p className="text-gray-600">
              Point your camera at any barcode or search by product name
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
              2
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Get the Facts</h3>
            <p className="text-gray-600">
              See Nutri-Score, calories, allergens, ingredients, and full nutrition details
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
              3
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Track Your Day</h3>
            <p className="text-gray-600">
              Log meals to your daily tracker and monitor your nutrition goals
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-gradient-to-br from-green-50 to-blue-50 rounded-3xl p-8 md:p-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            About NutriLens Pro
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            NutriLens Pro is your personal nutrition assistant that helps you make informed food choices.
            Simply scan any product barcode to instantly access comprehensive nutritional information,
            ingredients, allergens, and Nutri-Score ratings.
          </p>
          <p className="text-lg text-gray-600">
            Powered by Open Food Facts, the world's largest open food database,
            NutriLens Pro provides information for millions of products worldwide.
            All your data stays on your device — no account needed, no data uploaded.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
